import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";

/**
 * Service centralisé de logging d'erreurs
 * - En DEV : affiche dans la console
 * - En PROD : envoie vers Supabase + Sentry
 */

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Logger une erreur critique
 * @param message Message descriptif de l'erreur
 * @param error Objet Error ou message d'erreur
 * @param context Contexte additionnel (component, userId, etc.)
 */
export async function logError(
  message: string,
  error: Error | unknown,
  context?: ErrorContext
): Promise<void> {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  // En développement : afficher dans la console
  if (import.meta.env.DEV) {
    console.error(`[ERROR] ${message}`, {
      error: errorMessage,
      stack: errorStack,
      context,
    });
    return;
  }

  // En production : envoyer vers Sentry
  if (error instanceof Error) {
    Sentry.captureException(error, {
      contexts: { custom: { message, ...context } },
    });
  }

  // Envoyer vers Supabase (asynchrone, sans bloquer)
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase.from("error_logs").insert({
      error_message: `${message}: ${errorMessage}`,
      error_stack: errorStack || null,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: user?.id || context?.userId || null,
      component_stack: context?.component || null,
    });
  } catch (loggingError) {
    // Fallback silencieux si le logging échoue
    if (import.meta.env.DEV) {
      console.warn("Failed to log error to Supabase:", loggingError);
    }
  }
}

/**
 * Logger un message de debug (uniquement en DEV)
 * @param category Catégorie du log (VIDEO, NATIVE, etc.)
 * @param message Message à logger
 * @param data Données additionnelles
 */
export function debugLog(
  category: string,
  message: string,
  data?: any
): void {
  if (!import.meta.env.DEV) return;

  const emoji = {
    NATIVE: "📹",
    VIDEO: "🎥",
    AUTH: "🔐",
    PERFORMANCE: "⚡",
    PAYMENT: "💳",
  }[category] || "📝";

  console.log(`${emoji} [${category}] ${message}`, data || "");
}

/**
 * Logger un avertissement non critique
 * @param message Message d'avertissement
 * @param context Contexte additionnel
 */
export function logWarning(
  message: string,
  context?: ErrorContext
): void {
  if (import.meta.env.DEV) {
    console.warn(`[WARNING] ${message}`, context);
  }

  // En production, envoyer seulement vers Sentry
  if (!import.meta.env.DEV) {
    Sentry.captureMessage(message, {
      level: "warning",
      contexts: { custom: context },
    });
  }
}
